import { useDispatch, useSelector } from 'react-redux';

import {
  ExplodeIcon,
  Flex,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tooltip,
} from '@frontend/legacy-components';

import { explodedViewTool } from 'modules/common/viewer/cadViewer/data/actions';
import {
  getExplodedFactorForViewerId,
  getFocusedBodies,
  getIsExplodedViewActive,
} from 'modules/common/viewer/cadViewer/data/reducer';

import ViewerButton from '../components/ViewerButton';
import ViewerButtonTooltip from '../components/ViewerButtonTooltip';
import ViewerToolPanel from '../components/ViewerToolPanel';

export type ExplodedViewToolProps = {
  /** 3D CAD (STEP, etc.); hidden for DXF / 2D controls */
  isCad3d: boolean;
  /** Active file is an assembly (explode is supported) */
  isAssembly: boolean;
  /** `toolbar` — toggle in the tool strip; `floating` — amount slider below the strip */
  placement: 'toolbar' | 'floating';
};

/**
 * Exploded assembly view: toolbar toggle and, when active, the separation slider.
 * Two JSX nodes via `placement` because the tool strip and floating panel mount in different places.
 */
const ExplodedViewTool = ({
  isCad3d,
  isAssembly,
  placement,
}: ExplodedViewToolProps) => {
  const dispatch = useDispatch();
  const focusedBodies = useSelector(getFocusedBodies);
  const viewerId = focusedBodies[0];
  const factor = useSelector((state) =>
    getExplodedFactorForViewerId(state, viewerId),
  );
  const isExplodedViewActive = useSelector(getIsExplodedViewActive);

  const singleFocus = Boolean(focusedBodies?.length === 1);
  const canInteract = isAssembly && singleFocus;

  if (placement === 'toolbar') {
    if (!isCad3d) {
      return null;
    }

    if (!canInteract) {
      return (
        <Tooltip
          placement="bottom"
          label={
            isAssembly
              ? 'Select a single assembly in the tree'
              : 'Explode only available for assemblies'
          }
        >
          <ViewerButton
            disabled
            aria-label={
              isAssembly
                ? 'Select a single assembly in the tree'
                : 'Explode only available for assemblies'
            }
          >
            <ExplodeIcon boxSize={3} />
          </ViewerButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Explode tool"
            secondLine="Explode one level of the active assembly using the slider"
          />
        }
      >
        <ViewerButton
          isActive={isExplodedViewActive}
          aria-label="Explode tool"
          onClick={() => dispatch(explodedViewTool.toggle())}
        >
          <ExplodeIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
    );
  }

  if (!isCad3d || !isAssembly || !isExplodedViewActive || !singleFocus) {
    return null;
  }

  const value = factor || 0;

  return (
    <ViewerToolPanel>
      <Tooltip placement="top" label="Explode the active assembly.">
        <Flex align="center" p={0.5}>
          <Slider
            aria-label="Exploded view amount"
            p={0.5}
            focusThumbOnChange={false}
            min={0}
            max={2}
            step={0.05}
            value={value}
            onChange={(next) =>
              dispatch(
                explodedViewTool.receiveExplodedState({
                  assemblyInstanceId: viewerId,
                  factor: next,
                }),
              )
            }
            w="12.5rem"
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </Flex>
      </Tooltip>
    </ViewerToolPanel>
  );
};

export default ExplodedViewTool;
